import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdGetApp, MdCalendarToday } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import UsibrasLoader from '../../../components/UsibrasLoader';
import logo from 'assets/img/auth/usibras_ghana_limited_logo.jpeg';

const DailyReport = () => {
  const navigate = useNavigate();
  const [reportDate, setReportDate] = useState(new Date());
  const [productionData, setProductionData] = useState([]);
  const [gradeData, setGradeData] = useState([]);
  const [propertyData, setPropertyData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        const formattedDate = reportDate.toISOString().split('T')[0];
        const response = await axios.post('https://usibras-web-backend.onrender.com/api/product-report', {
          date: formattedDate,
        });

        if (response.data && response.data.data && response.data.data.summary) {
          const { product_type_totals, totals: apiTotals } = response.data.data.summary;
          
          const transformedData = Object.keys(product_type_totals).map(productName => {
            const product = product_type_totals[productName];
            return {
              product: productName,
              grade: product.grade,
              property_category: product.category,
              dayQty: parseInt(product.day.quantity, 10),
              dayPerc: parseFloat(product.day.percentage),
              weekQty: parseInt(product.week.quantity, 10),
              weekPerc: parseFloat(product.week.percentage),
              monthQty: parseInt(product.month.quantity, 10),
              monthPerc: parseFloat(product.month.percentage),
              yearQty: parseFloat(product.year.quantity),
              yearPerc: parseFloat(product.year.percentage),
            };
          });

          const currentTotals = {
            dayQty: parseInt(apiTotals.day_total_quantity, 10),
            weekQty: parseInt(apiTotals.week_total_quantity, 10),
            monthQty: parseInt(apiTotals.month_total_quantity, 10),
            yearQty: parseFloat(apiTotals.year_total_quantity),
          };
          
          setProductionData(transformedData);
          setTotals(currentTotals);

          // Calculate Grade Percentage Distribution
          const grades = [...new Set(transformedData.map(p => p.grade))].sort();
          const gradeDistribution = grades.map(grade => {
            const productsInGrade = transformedData.filter(p => p.grade === grade);
            
            const gradeDayQty = productsInGrade.reduce((sum, p) => sum + p.dayQty, 0);
            const gradeWeekQty = productsInGrade.reduce((sum, p) => sum + p.weekQty, 0);
            const gradeMonthQty = productsInGrade.reduce((sum, p) => sum + p.monthQty, 0);
            const gradeYearQty = productsInGrade.reduce((sum, p) => sum + p.yearQty, 0);

            return {
              grade,
              dayQty: gradeDayQty,
              dayPerc: currentTotals.dayQty > 0 ? (gradeDayQty / currentTotals.dayQty) * 100 : 0,
              weekQty: gradeWeekQty,
              weekPerc: currentTotals.weekQty > 0 ? (gradeWeekQty / currentTotals.weekQty) * 100 : 0,
              monthQty: gradeMonthQty,
              monthPerc: currentTotals.monthQty > 0 ? (gradeMonthQty / currentTotals.monthQty) * 100 : 0,
              yearQty: gradeYearQty,
              yearPerc: currentTotals.yearQty > 0 ? (gradeYearQty / currentTotals.yearQty) * 100 : 0,
            };
          });
          setGradeData(gradeDistribution);

          // Calculate Property Category Percentage Distribution
          const propertyCategories = [...new Set(transformedData.map(p => p.property_category))].sort();
          const propertyDistribution = propertyCategories.map(category => {
            if (!category) return null; // Skip if category is null or undefined
            const productsInCategory = transformedData.filter(p => p.property_category === category);
            
            const propDayQty = productsInCategory.reduce((sum, p) => sum + p.dayQty, 0);
            const propWeekQty = productsInCategory.reduce((sum, p) => sum + p.weekQty, 0);
            const propMonthQty = productsInCategory.reduce((sum, p) => sum + p.monthQty, 0);
            const propYearQty = productsInCategory.reduce((sum, p) => sum + p.yearQty, 0);

            return {
              property: category,
              dayQty: propDayQty,
              dayPerc: currentTotals.dayQty > 0 ? (propDayQty / currentTotals.dayQty) * 100 : 0,
              weekQty: propWeekQty,
              weekPerc: currentTotals.weekQty > 0 ? (propWeekQty / currentTotals.weekQty) * 100 : 0,
              monthQty: propMonthQty,
              monthPerc: currentTotals.monthQty > 0 ? (propMonthQty / currentTotals.monthQty) * 100 : 0,
              yearQty: propYearQty,
              yearPerc: currentTotals.yearQty > 0 ? (propYearQty / currentTotals.yearQty) * 100 : 0,
            };
          }).filter(Boolean); // Remove null entries
          setPropertyData(propertyDistribution);
        }
      } catch (error) {
        console.error('Error fetching report data:', error);
        toast.error('Failed to fetch report data. Please try again.');
        setProductionData([]);
        setGradeData([]);
        setPropertyData([]);
        setTotals({});
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportDate]);

  const downloadPdf = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 10;

    // Logo
    const img = new window.Image();
    img.src = logo;
    doc.addImage(img, 'JPEG', 10, y, 30, 30);

    // Title and Date
    doc.setFontSize(18);
    doc.text('Production Report', pageWidth / 2, y + 10, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Daily Report - FOLLOW-UP', pageWidth / 2, y + 18, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Date: ${reportDate.toLocaleDateString('en-CA')}`, pageWidth / 2, y + 26, { align: 'center' });

    autoTable(doc, {
      startY: y + 42,
      head: [['Product', 'Grade', 'Day(Qty)', '%Day', 'Week(Qty)', '%Week', 'Month(Qty)', '%Month', 'Year(Qty)', '%Year']],
      body: productionData.map(item => [
        item.product,
        item.grade,
        item.dayQty,
        item.dayPerc.toFixed(2),
        item.weekQty,
        item.weekPerc.toFixed(2),
        item.monthQty,
        item.monthPerc.toFixed(2),
        item.yearQty.toFixed(2),
        item.yearPerc.toFixed(2)
      ]),
      foot: [[
        'Total', '',
        totals.dayQty, totals.dayQty > 0 ? '100.00' : '0.00',
        totals.weekQty, totals.weekQty > 0 ? '100.00' : '0.00',
        totals.monthQty, totals.monthQty > 0 ? '100.00' : '0.00',
        totals.yearQty ? totals.yearQty.toFixed(2) : '0.00', totals.yearQty > 0 ? '100.00' : '0.00'
      ]],
      headStyles: { fillColor: [22, 160, 133] },
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
      theme: 'grid',
    });

    doc.setFontSize(16);
    doc.text('Grade Percentage Distributions', 14, doc.lastAutoTable.finalY + 15);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Grade', 'Day(Qty)', '%Day', 'Week(Qty)', '%Week', 'Month(Qty)', '%Month', 'Year(Qty)', '%Year']],
      body: gradeData.map(item => [
        item.grade,
        item.dayQty,
        item.dayPerc.toFixed(2),
        item.weekQty,
        item.weekPerc.toFixed(2),
        item.monthQty,
        item.monthPerc.toFixed(2),
        item.yearQty.toFixed(2),
        item.yearPerc.toFixed(2)
      ]),
      foot: [[
        'Total',
        totals.dayQty, totals.dayQty > 0 ? '100.00' : '0.00',
        totals.weekQty, totals.weekQty > 0 ? '100.00' : '0.00',
        totals.monthQty, totals.monthQty > 0 ? '100.00' : '0.00',
        totals.yearQty ? totals.yearQty.toFixed(2) : '0.00', totals.yearQty > 0 ? '100.00' : '0.00'
      ]],
      headStyles: { fillColor: [22, 160, 133] },
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
      theme: 'grid',
    });

    doc.setFontSize(16);
    doc.text('Percentage Distributions', 14, doc.lastAutoTable.finalY + 15);

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Property', 'Day(Qty)', '%Day', 'Week(Qty)', '%Week', 'Month(Qty)', '%Month', 'Year(Qty)', '%Year']],
        body: propertyData.map(item => [
            item.property,
            item.dayQty,
            item.dayPerc.toFixed(2),
            item.weekQty,
            item.weekPerc.toFixed(2),
            item.monthQty,
            item.monthPerc.toFixed(2),
            item.yearQty.toFixed(2),
            item.yearPerc.toFixed(2)
        ]),
        foot: [[
            'Total',
            totals.dayQty, totals.dayQty > 0 ? '100.00' : '0.00',
            totals.weekQty, totals.weekQty > 0 ? '100.00' : '0.00',
            totals.monthQty, totals.monthQty > 0 ? '100.00' : '0.00',
            totals.yearQty ? totals.yearQty.toFixed(2) : '0.00', totals.yearQty > 0 ? '100.00' : '0.00'
        ]],
        headStyles: { fillColor: [22, 160, 133] },
        footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
        theme: 'grid',
    });

    doc.save('daily-production-report.pdf');
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 self-start"
          >
            <MdArrowBack size={24} />
            <span className="font-medium">Back to Reports</span>
          </button>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full">
              <DatePicker
                selected={reportDate}
                onChange={(date) => setReportDate(date)}
                dateFormat="yyyy-MM-dd"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <MdCalendarToday className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={downloadPdf}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading || productionData.length === 0}
            >
              <MdGetApp className="mr-2" size={20} />
              <span>Download PDF</span>
            </button>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <img src={logo} alt="Usibras Ghana Limited Logo" className="h-20 w-auto mb-4 mx-auto" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Production Report</h1>
          <p className="text-base md:text-lg text-gray-600">Daily Report - FOLLOW-UP</p>
          <p className="text-sm md:text-base text-gray-500 mt-1">{`Date: ${reportDate.toLocaleDateString('en-CA')}`}</p>
        </div>

        {/* Report Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {loading ? (
             <div className="flex h-[400px] items-center justify-center">
                <UsibrasLoader text="Fetching report data..." />
             </div>
          ) : productionData.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Production Report Table */}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Product</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Grade</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Day(Qty)</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Day</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Week(Qty)</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Week</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Month(Qty)</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Month</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Year(Qty)</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Year</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productionData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.product}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.grade}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayPerc.toFixed(2)}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekPerc.toFixed(2)}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthPerc.toFixed(2)}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearQty.toFixed(2)}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearPerc.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-200">
                  <tr>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800" colSpan="2">Total</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty > 0 ? '100.00' : '0.00'}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty > 0 ? '100.00' : '0.00'}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty > 0 ? '100.00' : '0.00'}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty ? totals.yearQty.toFixed(2) : '0.00'}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty > 0 ? '100.00' : '0.00'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 px-4">
              <h3 className="text-lg font-medium text-gray-700">No data available for this date.</h3>
              <p className="text-sm text-gray-500">Please select another date.</p>
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="text-center mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">Grade Percentage Distributions</h2>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {loading ? (
              <div className="flex h-[300px] items-center justify-center">
                <UsibrasLoader text="Calculating grade distributions..." />
              </div>
            ) : gradeData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Grade</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Day(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Day</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Week(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Week</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Month(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Month</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Year(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Year</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {gradeData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.grade}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearQty.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearPerc.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-200">
                    <tr>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800">Total</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty ? totals.yearQty.toFixed(2) : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty > 0 ? '100.00' : '0.00'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 px-4">
                <h3 className="text-lg font-medium text-gray-700">No grade distribution data available.</h3>
              </div>
            )}
          </div>
        </div>

        {/* Property Category Percentage Distribution Table */}
        <div className="mt-8">
          <div className="text-center mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">Property Category Percentage Distributions</h2>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {loading ? (
              <div className="flex h-[300px] items-center justify-center">
                <UsibrasLoader text="Calculating property distributions..." />
              </div>
            ) : propertyData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Property</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Day(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Day</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Week(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Week</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Month(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Month</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Year(Qty)</th>
                      <th className="px-3 sm:px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">%Year</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {propertyData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.property}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.dayPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.weekPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthQty}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.monthPerc.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearQty.toFixed(2)}</td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{item.yearPerc.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-200">
                    <tr>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800">Total</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.dayQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.weekQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.monthQty > 0 ? '100.00' : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty ? totals.yearQty.toFixed(2) : '0.00'}</td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-800 text-right">{totals.yearQty > 0 ? '100.00' : '0.00'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 px-4">
                <h3 className="text-lg font-medium text-gray-700">No property distribution data available.</h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyReport; 